import {useState, lazy} from 'react'
import {AgentIcon} from '@primer/octicons-react'
import {AnchoredOverlay, Button, IconButton} from '@primer/react'
import type {Repository} from '@github-ui/current-repository'
import type {CreateTaskReference} from '@github-ui/agent-sessions/components/NewTaskChatInput'
import {useClientValue} from '@github-ui/use-client-value'

export type NewAgentTaskButtonProps = {
  isIconButton?: boolean
  currentRepo?: Repository
  currentReference?: CreateTaskReference
  hideRepoSelector?: boolean
  hideIcon?: boolean
  eventType: string
  size?: 'small' | 'medium' | 'large'
  tooltipDirection?: 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'
}

export {NewAgentTaskButton}
const LazyNewAgentTaskButtonContent = lazy(() =>
  // eslint-disable-next-line github/no-then
  import('./NewAgentTaskButtonContent').then(m => ({default: m.NewAgentTaskButtonContent})),
)

function NewAgentTaskButton({
  isIconButton,
  currentRepo,
  currentReference,
  hideRepoSelector,
  eventType,
  size,
  tooltipDirection,
}: NewAgentTaskButtonProps) {
  const [open, setOpen] = useState(false)
  const [isSSR] = useClientValue(() => false, true, [])

  return (
    <AnchoredOverlay
      width="large"
      align="end"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      renderAnchor={props => {
        return isIconButton ? (
          <IconButton
            icon={AgentIcon}
            aria-label="New agent task"
            // We shouldn't need this but the typing requires something
            aria-labelledby={undefined}
            size={size}
            tooltipDirection={tooltipDirection}
            {...props}
          />
        ) : (
          <Button {...props} leadingVisual={<AgentIcon />} size={size}>
            New agent task
          </Button>
        )
      }}
    >
      {!isSSR && (
        <LazyNewAgentTaskButtonContent
          currentRepo={currentRepo}
          currentReference={currentReference}
          hideRepoSelector={hideRepoSelector}
          setOpen={setOpen}
          eventType={eventType}
        />
      )}
    </AnchoredOverlay>
  )
}

try{ LazyNewAgentTaskButtonContent.displayName ||= 'LazyNewAgentTaskButtonContent' } catch {}
try{ NewAgentTaskButton.displayName ||= 'NewAgentTaskButton' } catch {}